/**
 * 产品管理页面组件。
 *
 * 该文件实现了 CRM 系统中的「产品管理」功能页面，包括产品的增删改查、
 * 产品分类管理、产品图片上传与排序、文档附件管理，以及网站内容重建触发。
 *
 * 主要导出内容：
 * - `CrmProductsPage` — 注册为 `<crm-products-page>` 自定义元素的 Lit 组件类
 * - `compressImage` — 图片压缩工具函数（模块内使用）
 *
 * 与其他模块的关系：
 * - 通过 `../api.js` 调用 `productApi`、`uploadProductImage`、`uploadProductDocument`、`websiteApi` 等 API
 * - 通过 `../store.js` 的 `authStore` 获取用户角色（用于控制「更新网站」按钮的显示权限）
 * - 依赖 `../components/` 下的公共组件（select、side-panel、loading-overlay、pagination、toast、confirm-dialog）
 *
 * 关键设计决策：
 * - 图片上传前在前端进行 WebP 压缩，最大尺寸 1920px，质量 0.82，减少带宽和存储
 * - 产品图片支持拖拽排序，排序结果即时同步后端
 * - 编辑面板使用侧边滑入式（crm-side-panel），而非全屏弹窗
 * - 网站重建功能仅对 admin 角色可见，采用「入队 + 轮询」模式异步等待构建结果
 * - 待上传文件（图片预览、文档）使用本地 URL.createObjectURL 生成临时预览，保存后释放
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { ProductListItem, ProductCategory, ProductImage, ProductDocument, ProductTranslation } from '../types.js';
import { BaseElement } from '../base-element.js';
import { productApi, uploadProductImage, uploadProductDocument, API_BASE_URL, websiteApi } from '../api.js';
import { toast } from '../components/toast.js';
import { isNetworkError } from '../utils/index.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { authStore } from '../store.js';
import { SUPPORTED_LANGUAGES, DEFAULT_LANG } from '@crm/shared/constants/languages.js';
import '../components/select.js';
import '../components/side-panel.js';
import '../components/loading-overlay.js';
import '../components/pagination.js';
import '../components/product-editor.js';

/** 每个产品最多允许上传的图片数量 */
const MAX_IMAGES = 10;
/** 编辑侧边面板的宽度（像素），较宽以容纳产品图片网格 */
const PANEL_WIDTH = 1000;
/** 图片压缩时的最大边长（像素），超出则等比缩小 */
const MAX_DIMENSION = 1920;
/** WebP 压缩质量因子（0~1），0.82 在文件大小和画质之间取得平衡 */
const WEBP_QUALITY = 0.82;

/**
 * 在浏览器端压缩图片文件。
 *
 * 将原始图片通过 Canvas 重新绘制为 WebP 格式：
 * 1. 如果图片任一边长超过 MAX_DIMENSION（1920px），按比例等比缩小
 * 2. 使用 Canvas.toBlob 以 WebP 格式和质量 WEBP_QUALITY 压缩
 * 3. 生成新的 File 对象，文件名后缀替换为 .webp
 *
 * 该函数用于产品图片上传前的预处理，减少网络传输量和服务器存储压力。
 *
 * @param file - 原始图片文件（支持任意浏览器原生支持的图片格式）
 * @returns 压缩后的 WebP 格式 File 对象
 * @rejects 图片加载失败或 Canvas 压缩失败时 reject
 */
function compressImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(img.src);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('WebP 压缩失败')); return; }
          const name = file.name.replace(/\.[^.]+$/, '.webp');
          resolve(new File([blob], name, { type: 'image/webp' }));
        },
        'image/webp',
        WEBP_QUALITY,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error('图片加载失败')); };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * 产品管理页面组件。
 *
 * 职责：
 * - 展示产品列表（卡片式网格布局），支持搜索和分类筛选
 * - 新增/编辑产品（侧边面板），包含名称、分类、描述、图片和文档
 * - 产品分类的增删改
 * - 产品图片的压缩上传、拖拽排序、删除
 * - 产品文档附件的上传和删除
 * - 触发网站内容重建（仅 admin 角色），含异步轮询构建状态
 *
 * 该组件注册为 `<crm-products-page>` 自定义元素。
 */
@customElement('crm-products-page')
export class CrmProductsPage extends BaseElement {
  /** 产品列表数据 */
  @state() private products: ProductListItem[] = [];
  /** 列表加载状态标志 */
  @state() private isLoading = false;
  /** 列表请求序号，仅允许最后一次筛选请求更新页面。 */
  private _listRequestId = 0;
  /** 列表数据总条数（用于分页） */
  @state() private total = 0;
  /** 当前页码 */
  @state() private currentPage = 1;
  /** 每页显示条数（卡片网格模式下为 20） */
  @state() private perPage = 20;
  /** 搜索关键词（产品名称） */
  @state() private searchQuery = '';
  /** 分类筛选 ID（0 表示全部分类） */
  @state() private categoryFilter = 0;

  // 分类
  /** 产品分类列表 */
  @state() private categories: ProductCategory[] = [];
  /** 是否显示分类管理弹窗 */
  @state() private showCatModal = false;
  /** 分类表单 - 名称输入值 */
  @state() private catFormName = '';
  /** 分类表单 - 正在编辑的分类 ID（0 表示新增） */
  @state() private catEditingId = 0;
  /** 分类表单 - 排序值 */
  @state() private catFormSort = 0;

  // 编辑态
  /** 当前正在编辑的产品 ID（0 表示新增） */
  private editingId = 0;
  /** 编辑表单 - 产品名称 */
  private formName = '';
  /** 编辑表单 - 分类 ID */
  private formCategoryId = 0;
  /** 编辑表单 - 产品描述 */
  private formDescription = '';
  /** 多语言翻译：按 lang 存储 {name, description}，不含默认语言（默认语言走 formName/formDescription） */
  @state() private translations: Record<string, { name: string; description: string }> = {};
  /** 当前正在编辑的翻译语言码（默认 es） */
  @state() private activeTransLang: string = '';
  /** 已保存的产品图片列表（从后端获取） */
  private panelImages: ProductImage[] = [];
  /** 已保存的产品文档列表（从后端获取） */
  private panelDocs: ProductDocument[] = [];
  /** 待上传的图片文件列表（含本地预览 URL 和主图标记） */
  private pendingImageFiles: Array<{ file: File; isPrimary: boolean; previewUrl: string }> = [];
  /** 待上传的文档文件列表 */
  private pendingDocFiles: File[] = [];
  /** 标记为待删除的已保存图片 ID 列表（保存时统一提交删除） */
  private removedImageIds: number[] = [];
  /** 标记为待删除的已保存文档 ID 列表（保存时统一提交删除） */
  private removedDocIds: number[] = [];
  /** 当前拖拽的图片索引（用于拖拽排序） */
  private dragImgIdx = -1;
  /** 标记鼠标是否在遮罩层上按下（用于分类弹窗的点击遮罩关闭） */
  private _backdropMousedown = false;

  /** 当前用户角色（从 authStore 获取，用于控制 admin 权限功能） */
  @state() private userRole: string | null = null;
  /** 网站重建状态：idle=空闲 / enqueue=已入队 / polling=轮询中 / done=完成 */
  @state() private websiteRebuildState: 'idle' | 'enqueue' | 'polling' | 'done' = 'idle';
  /** 网站重建状态轮询定时器 ID */
  private _rebuildPollTimer: number | null = null;
  /** authStore 变更订阅取消函数 */
  private _unsubscribe?: () => void;

  /** 判断当前用户是否为管理员（控制「更新网站」按钮的显示） */
  private get isAdmin() { return this.userRole === 'admin'; }

  static styles = css`
    .product-card {
      transition: box-shadow 0.15s ease, transform 0.15s ease;
    }
    .product-card:hover {
      box-shadow: rgba(0,0,0,0.08) 0px 3px 6px, rgba(0,0,0,0.07) 0px 2px 4px;
      transform: translateY(-1px);
    }
    .image-thumb {
      width: 80px; height: 80px;
      object-fit: cover;
      border-radius: 6px;
      border: 1px solid #e0e1e6;
    }
    .image-thumb.primary {
      border-color: #0d74ce;
      box-shadow: 0 0 0 2px rgba(13,116,206,0.2);
    }
  `;

  /**
   * 组件挂载时的生命周期回调。
   *
   * 调用父类方法后：
   * 1. 加载产品列表和分类列表
   * 2. 从 authStore 获取当前用户角色
   * 3. 订阅 authStore 变化，实时更新 userRole 状态
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadProducts();
    this.loadCategories();
    this.userRole = authStore.state.user?.role || null;
    this._unsubscribe = authStore.subscribe((state) => {
      this.userRole = state.user?.role || null;
    });
  }

  /**
   * 组件卸载时的生命周期回调。
   *
   * 调用父类方法后：
   * 1. 清理网站重建轮询定时器（避免内存泄漏和无效请求）
   * 2. 取消 authStore 订阅
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._rebuildPollTimer !== null) {
      window.clearInterval(this._rebuildPollTimer);
      this._rebuildPollTimer = null;
    }
    this._unsubscribe?.();
  }

  /**
   * 触发网站内容重建。
   *
   * 流程：
   * 1. 防止重复触发（enqueue/polling 状态下直接返回）
   * 2. 弹出确认对话框
   * 3. 调用 websiteApi.rebuild() 提交重建请求
   * 4. 根据 res.deploy 判断是「构建+发布」还是「仅构建」
   * 5. 进入 polling 状态，启动轮询定时器查询构建结果
   *
   * 失败时恢复为 idle 状态并显示错误 toast。
   */
  private async handleRebuild() {
    // 防止重复触发
    if (this.websiteRebuildState === 'enqueue' || this.websiteRebuildState === 'polling') return;
    if (!(await showConfirm('确认要更新网站内容吗？', { variant: 'warning' }))) return;
    this.websiteRebuildState = 'enqueue';
    try {
      const res = await websiteApi.rebuild();
      // deploy === 'published' 表示 Cloudflare 凭证齐全，构建后会自动发布
      const deployNote = res.deploy === 'published' ? '（构建 + 发布）' : '（仅构建，Cloudflare 凭证未齐）';
      toast.info(`${res.message}${deployNote}`);
      this.websiteRebuildState = 'polling';
      this._startRebuildPoll();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '请求失败';
      if (!isNetworkError(msg)) toast.error(msg);
      this.websiteRebuildState = 'idle';
    }
  }

  /**
   * 启动网站重建状态轮询。
   *
   * 每 3 秒查询一次构建状态，根据返回结果判断：
   * - `running`：仍在构建中，继续轮询
   * - `success`：构建成功，停止轮询并显示成功 toast
   * - `failed`/`error`：构建失败，停止轮询并显示错误 toast（含错误信息或 stderr 尾行）
   * - `skipped`：被跳过（另一个构建正在进行），停止轮询并显示警告
   * - 无状态：等待，超过 30 次（约 90 秒）超时停止
   *
   * 轮询通过 setInterval 实现，定时器 ID 存入 _rebuildPollTimer。
   */
  private _startRebuildPoll() {
    if (this._rebuildPollTimer !== null) return;
    let attempts = 0;
    this._rebuildPollTimer = window.setInterval(async () => {
      attempts++;
      try {
        const s = await websiteApi.status();
        // 构建仍在进行中，继续等待
        if (s.running) return;
        const last = s.last;
        // 无历史构建记录，等待获取状态
        if (!last || !last.status) {
          if (attempts > 30) this._stopRebuildPoll('未获取到任务状态');
          return;
        }
        // last.status 仍为 running 表示任务排队中
        if (last.status === 'running') return;
        if (last.status === 'success') {
          const deployLabel = last.deploy === 'published'
            ? '已发布到 Cloudflare Pages'
            : '构建成功（未发布到 Cloudflare）';
          this._stopRebuildPoll(deployLabel, 'success');
        } else if (last.status === 'failed' || last.status === 'error') {
          // 优先使用 error 字段，其次取 stderr 尾行，最后使用 phase 信息
          const err = last.error || last.stderr_tail?.split('\n').pop() || `构建失败 (phase=${last.phase})`;
          this._stopRebuildPoll(err, 'error');
        } else if (last.status === 'skipped') {
          this._stopRebuildPoll('另一个构建任务正在进行', 'warn');
        }
      } catch {
        if (attempts > 30) this._stopRebuildPoll('无法查询任务状态');
      }
    }, 3000);
  }

  /**
   * 停止网站重建状态轮询并显示最终结果。
   *
   * 清除轮询定时器，设置状态为 done，根据 kind 显示不同类型的 toast。
   * 4 秒后自动将状态重置为 idle（若仍为 done）。
   *
   * @param message - 结果描述信息
   * @param kind - 结果类型：success/error/warn/info，决定 toast 样式
   */
  private _stopRebuildPoll(message: string, kind: 'success' | 'error' | 'warn' | 'info' = 'info') {
    if (this._rebuildPollTimer !== null) {
      window.clearInterval(this._rebuildPollTimer);
      this._rebuildPollTimer = null;
    }
    this.websiteRebuildState = 'done';
    if (kind === 'success') toast.success(`网站更新完成 — ${message}`);
    else if (kind === 'error') toast.error(`网站更新失败 — ${message}`);
    else if (kind === 'warn') toast.warning(`网站更新 — ${message}`);
    else toast.info(`网站更新 — ${message}`);
    window.setTimeout(() => {
      if (this.websiteRebuildState === 'done') {
        this.websiteRebuildState = 'idle';
      }
    }, 4000);
  }

  // ======== 数据加载 ========

  /**
   * 加载产品列表数据。
   *
   * 根据当前页码、分类筛选和搜索关键词从后端获取产品列表。
   * categoryFilter 和 searchQuery 为 0/空时不传对应参数。
   * 结果存入 products 和 total 状态。
   */
  private async loadProducts() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const res = await productApi.list({
        page: this.currentPage, per_page: this.perPage,
        category_id: this.categoryFilter || undefined,
        keyword: this.searchQuery || undefined,
      });
      if (requestId !== this._listRequestId) return;
      this.products = res.data;
      this.total = res.pagination.total;
    } catch (e: unknown) {
      if (requestId !== this._listRequestId) return;
      const msg = e instanceof Error ? e.message : '加载产品失败';
      if (!isNetworkError(msg)) toast.error(msg);
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 加载产品分类列表。
   *
   * 从后端获取全部分类数据，存入 categories 状态。
   * 失败时静默忽略（不影响主列表展示）。
   */
  private async loadCategories() {
    try { this.categories = await productApi.listCategories(); } catch { /* */ }
  }

  /**
   * 搜索输入事件处理。
   *
   * 实时更新 searchQuery 状态，但不立即触发查询（需按回车或点击搜索按钮）。
   *
   * @param e - 输入事件对象
   */
  private handleSearch(e: Event) {
    this.searchQuery = (e.target as HTMLInputElement).value;
  }

  /**
   * 执行搜索查询。
   *
   * 重置页码为第 1 页后重新加载产品列表。
   */
  private async doSearch() { this.currentPage = 1; await this.loadProducts(); }

  /**
   * 清空搜索关键词并重新查询。
   */
  private clearSearch() { this.searchQuery = ''; this.doSearch(); }

  /**
   * 按分类筛选产品列表。
   *
   * @param catId - 分类 ID（0 表示全部分类）
   */
  private async filterByCat(catId: number) {
    this.categoryFilter = catId;
    this.currentPage = 1;
    await this.loadProducts();
  }

  /**
   * 产品列表翻页处理。
   *
   * @param p - 目标页码
   */
  private handlePage(p: number) { this.currentPage = p; this.loadProducts(); }

  // ======== 分类管理 ========

  /**
   * 打开分类管理弹窗。
   *
   * 若传入已有分类则为编辑模式，否则为新增模式。
   *
   * @param cat - 可选的已有分类对象；不传表示新增
   */
  private openCatModal(cat?: ProductCategory) {
    this.catEditingId = cat?.id || 0;
    this.catFormName = cat?.name || '';
    this.catFormSort = cat?.sort_order || 0;
    this.showCatModal = true;
  }

  /**
   * 保存分类（新增或更新）。
   *
   * 校验分类名称非空后，根据 catEditingId 判断是创建还是更新。
   * 成功后关闭弹窗并重新加载分类列表。
   */
  private async saveCategory() {
    if (!this.catFormName.trim()) { toast.error('请输入分类名称'); return; }
    try {
      if (this.catEditingId) {
        await productApi.updateCategory(this.catEditingId, { name: this.catFormName.trim(), sort_order: this.catFormSort });
      } else {
        await productApi.createCategory(this.catFormName.trim());
      }
      toast.success(this.catEditingId ? '分类已更新' : '分类已创建');
      this.showCatModal = false;
      await this.loadCategories();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '操作失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 删除指定分类。
   *
   * 弹出确认对话框，删除后如果当前筛选的正是该分类则重置筛选条件，
   * 然后重新加载分类列表和产品列表。
   *
   * @param id - 要删除的分类 ID
   */
  private async deleteCategory(id: number) {
    const ok = await showConfirm('确定要删除该分类吗？');
    if (!ok) return;
    try {
      await productApi.deleteCategory(id);
      toast.success('分类已删除');
      // 如果当前筛选的分类正好被删除，重置筛选条件
      if (this.categoryFilter === id) this.categoryFilter = 0;
      await this.loadCategories();
      await this.loadProducts();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '删除失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  // ======== 产品编辑面板 ========

  /**
   * 打开产品编辑侧边面板。
   *
   * 流程：
   * 1. 设置 editingId（0 表示新增模式）
   * 2. 释放之前待上传图片的 ObjectURL（避免内存泄漏）
   * 3. 重置所有编辑状态
   * 4. 若为编辑模式（有 id），从后端加载产品详情（名称、分类、描述、图片、文档）
   * 5. 调用 side-panel 组件的 show 方法打开面板
   * 6. 等待下一帧渲染后触发 requestUpdate
   *
   * @param id - 产品 ID；不传表示新增
   */
  private async openPanel(id?: number) {
    this.editingId = id || 0;
    // 释放之前待上传图片的 ObjectURL，避免内存泄漏
    for (const pf of this.pendingImageFiles) URL.revokeObjectURL(pf.previewUrl);
    this.panelImages = [];
    this.panelDocs = [];
    this.pendingImageFiles = [];
    this.pendingDocFiles = [];
    this.removedImageIds = [];
    this.removedDocIds = [];
    this.formDescription = '';
    this.translations = {};
    this.activeTransLang = this.firstTransLang();

    if (id) {
      try {
        const detail = await productApi.getDetail(id);
        this.formName = detail.name;
        this.formCategoryId = detail.category_id;
        this.formDescription = detail.description || '';
        this.panelImages = detail.images || [];
        this.panelDocs = detail.documents || [];
        const transMap: Record<string, { name: string; description: string }> = {};
        for (const t of (detail.translations || [])) {
          transMap[t.lang] = { name: t.name, description: t.description };
        }
        this.translations = transMap;
        // 选中已有翻译的第一个语言，否则选第一个可翻译语言
        const existing = Object.keys(transMap);
        this.activeTransLang = existing.length ? existing[0] : this.firstTransLang();
      } catch (e: any) { toast.error(e.message || '加载产品失败'); return; }
    } else {
      this.formName = '';
      this.formCategoryId = 0;
    }

    const panel = this.shadowRoot!.querySelector('#product-panel') as any;
    panel?.show(id ? '编辑产品' : '新增产品', PANEL_WIDTH);

    // 等待 panel open 状态更新
    requestAnimationFrame(() => this.requestUpdate());
  }

  /**
   * 关闭产品编辑侧边面板。
   *
   * 调用 side-panel 组件的 close 方法后触发重渲染。
   */
  private closePanel() {
    const panel = this.shadowRoot!.querySelector('#product-panel') as any;
    panel?.close();
    this.requestUpdate();
  }

  /** 返回第一个非默认语言的语言码（用于翻译区默认选中） */
  private firstTransLang(): string {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code !== DEFAULT_LANG);
    return lang ? lang.code : '';
  }

  /** 可翻译语言选项（排除默认语言） */
  private get transLangOptions() {
    return SUPPORTED_LANGUAGES.filter(l => l.code !== DEFAULT_LANG);
  }

  /** 当前语言对应的翻译数据（若无则空对象） */
  private get activeTrans(): { name: string; description: string } {
    return this.translations[this.activeTransLang] || { name: '', description: '' };
  }

  /** 判断指定语言是否已填写内容，用于在语言标签上显示完成状态。 */
  private hasTranslation(lang: string): boolean {
    const value = this.translations[lang];
    return Boolean(value?.name.trim() || value?.description.trim());
  }

  /** 切换当前编辑的翻译语言，并确保该语言有初始空数据 */
  private setActiveTransLang(lang: string) {
    this.activeTransLang = lang;
    if (!this.translations[lang]) {
      this.translations = { ...this.translations, [lang]: { name: '', description: '' } };
    }
    this.requestUpdate();
  }

  /** 更新当前语言翻译的 name 或 description 字段 */
  private updateTransField(field: 'name' | 'description', value: string) {
    const cur = this.activeTrans;
    this.translations = {
      ...this.translations,
      [this.activeTransLang]: { ...cur, [field]: value },
    };
    this.requestUpdate();
  }

  /**
   * 保存产品（新增或更新）。
   *
   * 流程：
   * 1. 校验产品名称非空
   * 2. 新增模式：调用 create 获取新 productId；编辑模式：调用 update
   * 3. 依次提交删除标记（已保存图片/文档的删除）
   * 4. 依次上传待上传的图片文件（含主图标记）
   * 5. 依次上传待上传的文档文件
   * 6. 释放所有待上传图片的 ObjectURL
   * 7. 关闭面板并重新加载产品列表
   *
   * 注意：删除和上传操作采用尽力而为策略（catch 忽略），不阻断后续操作。
   */
  private async saveProduct() {
    if (!this.formName.trim()) { toast.error('请输入产品名称'); return; }
    // 收集非默认语言且 name 非空的翻译
    const translations: ProductTranslation[] = Object.entries(this.translations)
      .map(([lang, v]) => ({ lang, name: v.name, description: v.description }))
      .filter(t => t.name.trim() !== '');
    try {
      let productId = this.editingId;
      if (productId) {
        await productApi.update(productId, {
          name: this.formName.trim(), category_id: this.formCategoryId,
          description: this.formDescription,
        });
      } else {
        const r = await productApi.create({
          name: this.formName.trim(), category_id: this.formCategoryId,
          description: this.formDescription,
        });
        productId = r.id;
      }
      // 翻译使用完整集合保存，清空某语言后会同步软删除旧记录。
      await productApi.replaceTranslations(productId, translations);
      // 依次删除标记为已删除的图片
      for (const imgId of this.removedImageIds) {
        try { await productApi.deleteImage(productId, imgId); } catch { /* */ }
      }
      // 依次删除标记为已删除的文档
      for (const docId of this.removedDocIds) {
        try { await productApi.deleteDoc(productId, docId); } catch { /* */ }
      }
      // 依次上传待上传的图片文件
      for (const pf of this.pendingImageFiles) {
        try { await uploadProductImage(productId, pf.file, pf.isPrimary); } catch { /* */ }
      }
      // 依次上传待上传的文档文件
      for (const f of this.pendingDocFiles) {
        try { await uploadProductDocument(productId, f); } catch { /* */ }
      }
      // 释放待上传图片的 ObjectURL
      for (const pf of this.pendingImageFiles) URL.revokeObjectURL(pf.previewUrl);
      toast.success(this.editingId ? '产品已更新' : '产品已创建');
      this.closePanel();
      await this.loadProducts();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '保存失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 删除指定产品。
   *
   * 弹出确认对话框，确认后调用 API 删除产品并重新加载列表。
   *
   * @param id - 要删除的产品 ID
   */
  private async deleteProduct(id: number) {
    const ok = await showConfirm('确定要删除该产品吗？');
    if (!ok) return;
    try {
      await productApi.delete(id);
      toast.success('产品已删除');
      await this.loadProducts();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '删除失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  // ======== 图片操作 ========

  /**
   * 处理图片选择事件。
   *
   * 流程：
   * 1. 计算当前图片数量（已保存 - 已删除 + 待上传）和剩余可上传数量
   * 2. 若已达上限（MAX_IMAGES）则提示并返回
   * 3. 截取不超过剩余数量的文件
   * 4. 若当前没有任何图片，第一张自动设为主图
   * 5. 对每个文件调用 compressImage 进行 WebP 压缩
   * 6. 生成本地预览 URL 并存入 pendingImageFiles
   *
   * 压缩失败的文件会显示错误 toast 但不阻断其他文件处理。
   *
   * @param e - 文件选择 change 事件
   */
  private async handleImageSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;
    // 计算当前有效图片数量：已保存 - 已删除 + 待上传
    const currentCount = this.panelImages.length + this.pendingImageFiles.length - this.removedImageIds.length;
    const remaining = MAX_IMAGES - currentCount;
    if (remaining <= 0) { toast.error(`最多上传 ${MAX_IMAGES} 张图片`); input.value = ''; return; }
    const files = Array.from(input.files).slice(0, remaining);
    // 若当前没有任何图片（已保存和待上传都为空），第一张自动设为主图
    const isFirst = this.panelImages.length === 0 && this.pendingImageFiles.length === 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const compressed = await compressImage(files[i]);
        const previewUrl = URL.createObjectURL(compressed);
        this.pendingImageFiles.push({ file: compressed, isPrimary: isFirst && i === 0, previewUrl });
      } catch {
        toast.error(`图片压缩失败: ${files[i].name}`);
      }
    }
    input.value = '';
    this.requestUpdate();
  }

  /**
   * 移除待上传的图片文件。
   *
   * 释放该图片的 ObjectURL 后从 pendingImageFiles 中删除。
   *
   * @param idx - pendingImageFiles 数组中的索引
   */
  private removePendingImage(idx: number) {
    URL.revokeObjectURL(this.pendingImageFiles[idx].previewUrl);
    this.pendingImageFiles.splice(idx, 1);
    this.requestUpdate();
  }

  /**
   * 标记已保存的图片为待删除。
   *
   * 将图片 ID 加入 removedImageIds（保存时统一提交删除），
   * 并从 panelImages 中过滤掉（UI 立即隐藏）。
   *
   * @param imgId - 要删除的已保存图片 ID
   */
  private markRemovedImage(imgId: number) {
    this.removedImageIds.push(imgId);
    this.panelImages = this.panelImages.filter(i => i.id !== imgId);
    this.requestUpdate();
  }

  /**
   * 图片拖拽开始事件处理。
   *
   * 记录被拖拽图片的索引，供 drop 时使用。
   *
   * @param idx - 被拖拽图片的索引
   */
  private handleImageDragStart(idx: number) { this.dragImgIdx = idx; }

  /**
   * 图片拖拽放置事件处理。
   *
   * 将被拖拽的图片移动到目标位置，重新排序所有图片的 sort_order，
   * 并异步调用后端 API 持久化排序结果。
   *
   * @param idx - 放置目标位置的索引
   */
  private handleImageDrop(idx: number) {
    // 拖拽源和目标相同，或未开始拖拽，直接返回
    if (this.dragImgIdx < 0 || this.dragImgIdx === idx) { this.dragImgIdx = -1; return; }
    const imgs = [...this.panelImages];
    const [moved] = imgs.splice(this.dragImgIdx, 1);
    imgs.splice(idx, 0, moved);
    // 重新分配 sort_order（从 1 开始）
    this.panelImages = imgs.map((img, i) => ({ ...img, sort_order: i + 1 }));
    this.dragImgIdx = -1;
    // 异步同步排序到后端（不等待，不阻断 UI）
    productApi.reorderImages(this.editingId, this.panelImages.map(i => i.id)).catch(() => {});
    this.requestUpdate();
  }

  // ======== 文档操作 ========

  /**
   * 处理文档附件选择事件。
   *
   * 将选中的所有文件加入 pendingDocFiles 待上传列表。
   * 与图片不同，文档不在前端进行压缩。
   *
   * @param e - 文件选择 change 事件
   */
  private handleDocSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;
    for (const f of Array.from(input.files)) this.pendingDocFiles.push(f);
    input.value = '';
    this.requestUpdate();
  }

  /**
   * 移除待上传的文档文件。
   *
   * @param idx - pendingDocFiles 数组中的索引
   */
  private removePendingDoc(idx: number) {
    this.pendingDocFiles.splice(idx, 1);
    this.requestUpdate();
  }

  /**
   * 标记已保存的文档为待删除。
   *
   * 将文档 ID 加入 removedDocIds（保存时统一提交删除），
   * 并从 panelDocs 中过滤掉（UI 立即隐藏）。
   *
   * @param docId - 要删除的已保存文档 ID
   */
  private markRemovedDoc(docId: number) {
    this.removedDocIds.push(docId);
    this.panelDocs = this.panelDocs.filter(d => d.id !== docId);
    this.requestUpdate();
  }

  // ======== 渲染 ========

  /**
   * 主渲染方法。
   *
   * 页面结构自上而下：
   * 1. 加载遮罩（crm-loading-overlay）
   * 2. 顶部操作栏：搜索框、分类筛选下拉、更新网站按钮（仅 admin）、新增产品按钮、分类管理按钮
   * 3. 白色卡片内容区：产品卡片网格（auto-fill 自适应列宽，最小 240px）
   * 4. 分页组件
   * 5. 分类管理弹窗（条件渲染）
   * 6. 编辑侧边面板（crm-side-panel）：名称、分类、描述、图片网格（拖拽排序）、文档列表、底部操作按钮
   *
   * activeImageCount 计算当前有效图片总数（已保存 - 已删除 + 待上传），用于显示和限制上传。
   *
   * @returns Lit html 模板
   */
  render() {
    // 计算当前有效图片数量：已保存 - 已删除 + 待上传
    const activeImageCount = this.panelImages.length - this.removedImageIds.length + this.pendingImageFiles.length;

    return html`
      <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>

      <div class="flex flex-col h-full">
        <!-- 顶部操作栏 -->
        <div class="flex items-center justify-between mb-2 flex-shrink-0 gap-3 flex-wrap">
          <div class="flex items-center gap-2">
            <div class="w-72 h-[32px] flex items-center rounded border border-[#d9d9e0] bg-white focus-within:border-[#0d74ce]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 ml-3 shrink-0 text-[#b0b4ba]"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="搜索产品..." .value=${this.searchQuery}
                @input=${this.handleSearch} @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.doSearch()}
                class="flex-1 h-full px-2 text-sm bg-transparent border-none outline-none placeholder:text-[#b0b4ba]" />
              ${this.searchQuery ? html`
                <button class="shrink-0 mr-1 w-5 h-5 flex items-center justify-center rounded-full text-[#b0b4ba] hover:text-[#1c2024] hover:bg-[#e0e1e6] transition-colors"
                  @click=${this.clearSearch}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              ` : ''}
            </div>
            <crm-select .value=${String(this.categoryFilter)}
              .options=${[{ label: '全部分类', value: '0' }, ...this.categories.map(c => ({ label: c.name, value: String(c.id) }))]}
              fitContent
              @change=${(e: CustomEvent) => this.filterByCat(parseInt(e.detail.value) || 0)}>
            </crm-select>
          </div>
          <div class="flex items-center gap-2">
            ${this.isAdmin ? html`
              <button @click=${this.handleRebuild}
                ?disabled=${this.websiteRebuildState === 'enqueue' || this.websiteRebuildState === 'polling'}
                class="flex items-center gap-1.5 px-4 py-1.5 bg-white text-[#1c2024] rounded text-sm font-medium border border-[#d9d9e0] hover:border-[#b0b4ba] transition-colors ${this.websiteRebuildState === 'enqueue' || this.websiteRebuildState === 'polling' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}"
                title=${this.websiteRebuildState === 'polling' ? '正在更新网站...' : '重新构建并发布网站'}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 ${this.websiteRebuildState === 'enqueue' || this.websiteRebuildState === 'polling' ? 'animate-spin' : ''}"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                ${this.websiteRebuildState === 'enqueue' || this.websiteRebuildState === 'polling' ? '更新中...' : '更新网站'}
              </button>
            ` : ''}
            <button @click=${() => this.openPanel()}
              class="flex items-center gap-1.5 px-5 py-1.5 bg-[#000] text-white rounded text-sm font-medium hover:opacity-90 transition-opacity">
              <span class="text-lg leading-none">+</span> 新增产品
            </button>
            <button @click=${() => this.openCatModal()}
              class="flex items-center gap-1.5 px-5 py-1.5 bg-white text-[#1c2024] rounded text-sm font-medium border border-[#d9d9e0] hover:border-[#b0b4ba] transition-colors">
              分类管理
            </button>
          </div>
        </div>

        <!-- 白色卡片内容区 -->
        <div class="flex-1 min-h-0">
          <div class="bg-surface rounded-md shadow-lg flex flex-col h-full overflow-hidden">
            <div class="flex-1 overflow-auto p-5">
              ${this.products.length === 0 ? html`
                <div class="flex flex-col items-center justify-center h-64 text-[#b0b4ba]">
                  <div class="text-4xl mb-3">📦</div>
                  <div class="text-sm">暂无产品，点击"新增产品"开始</div>
                </div>
              ` : html`
                <div class="grid gap-5" style="grid-template-columns:repeat(auto-fill,minmax(240px,1fr));">
                  ${this.products.map(p => {
                    const imgSrc = p.primary_image
                      ? `${API_BASE_URL}/products/images/${p.primary_image.filename}`
                      : '';
                    return html`
                      <div class="product-card bg-white rounded-md border border-[#e0e1e6] overflow-hidden cursor-pointer"
                        @click=${() => this.openPanel(p.id)}>
                        ${imgSrc ? html`
                          <div class="aspect-[16/10] bg-[#f0f0f3] overflow-hidden">
                            <img src=${imgSrc} alt=${p.name} class="w-full h-full object-cover" />
                          </div>
                        ` : html`
                          <div class="aspect-[16/10] bg-[#f0f0f3] flex items-center justify-center text-[#b0b4ba] text-3xl">📦</div>
                        `}
                        <div class="p-3.5">
                          <div class="font-semibold text-[#1c2024] text-sm truncate">${p.name}</div>
                          <div class="flex items-center justify-between mt-1.5">
                            <span class="text-xs text-[#60646c]">${p.category_name || '未分类'}</span>
                            <span class="text-xs text-[#b0b4ba]">${p.image_count}张图</span>
                          </div>
                        </div>
                      </div>`;
                  })}
                </div>
              `}
            </div>

            <!-- 分页 -->
            <crm-pagination
              .current=${this.currentPage}
              .perPage=${this.perPage}
              .total=${this.total}
              @page-change=${(e: CustomEvent) => this.handlePage(e.detail.page)}
            ></crm-pagination>
          </div>
        </div>
      </div>

      <!-- 分类弹窗 -->
      ${this.showCatModal ? html`
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/30" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.showCatModal = false; }}>
          <div class="bg-white rounded-md shadow-lg p-6 w-[420px]" @click=${(e: Event) => e.stopPropagation()}>
            <h3 class="text-lg font-semibold text-[#1c2024] mb-4">${this.catEditingId ? '编辑分类' : '新增分类'}</h3>
            <input type="text" placeholder="分类名称" .value=${this.catFormName}
              @input=${(e: Event) => this.catFormName = (e.target as HTMLInputElement).value}
              class="w-full px-3 py-1.5 border border-[#d9d9e0] rounded text-sm outline-none focus:border-[#0d74ce] mb-4" />
            <div class="flex justify-end gap-2">
              <button @click=${() => this.showCatModal = false}
                class="px-4 py-1.5 text-[#60646c] text-sm rounded border border-[#d9d9e0] hover:bg-[#f0f0f3]">取消</button>
              <button @click=${this.saveCategory}
                class="px-4 py-1.5 bg-[#000] text-white text-sm rounded font-medium">保存</button>
            </div>
            ${this.categories.length > 0 ? html`
              <div class="mt-5 border-t border-[#e0e1e6] pt-4">
                <div class="text-xs text-[#b0b4ba] mb-2">现有分类</div>
                ${this.categories.map(c => html`
                  <div class="flex items-center justify-between py-1.5">
                    <span class="text-sm text-[#1c2024]">${c.name}</span>
                    <div class="flex gap-1">
                      <button @click=${() => this.openCatModal(c)} class="text-xs text-[#0d74ce] px-2 py-0.5 rounded hover:bg-[#f0f0f3]">编辑</button>
                      <button @click=${() => this.deleteCategory(c.id)} class="text-xs text-[#eb8e90] px-2 py-0.5 rounded hover:bg-[#f0f0f3]">删除</button>
                    </div>
                  </div>
                `)}
              </div>
            ` : ''}
          </div>
        </div>
      ` : ''}

      <!-- 编辑侧边面板 -->
      <crm-side-panel id="product-panel" @crm-panel-close=${this.closePanel}>
        <div class="flex flex-col" style="min-height:0;">
          <div class="flex-1 space-y-6" style="overflow-y:auto;padding-right:4px;">
            <!-- 名称 -->
            <div>
              <label class="block text-sm font-medium text-[#1c2024] mb-1.5">产品名称 <span class="text-[#eb8e90]">*</span></label>
              <input type="text" .value=${this.formName}
                @input=${(e: Event) => this.formName = (e.target as HTMLInputElement).value}
                class="w-full px-3 py-1.5 border border-[#d9d9e0] rounded text-sm outline-none focus:border-[#0d74ce]"
                placeholder="输入产品名称" />
            </div>

            <!-- 分类 -->
            <div>
              <label class="block text-sm font-medium text-[#1c2024] mb-1.5">分类</label>
              <crm-select .value=${String(this.formCategoryId)}
                .options=${[{ label: '未分类', value: '0' }, ...this.categories.map(c => ({ label: c.name, value: String(c.id) }))]}
                fullWidth
                @change=${(e: CustomEvent) => this.formCategoryId = parseInt(e.detail.value) || 0}>
              </crm-select>
            </div>

            <!-- 描述 -->
            <div>
              <label class="block text-sm font-medium text-[#1c2024] mb-1.5">产品描述</label>
              <crm-product-editor
                .value=${this.formDescription}
                .minHeight=${420}
                placeholder="输入产品描述..."
                @input=${(e: Event) => this.formDescription = (e as CustomEvent).detail.value as string}></crm-product-editor>
            </div>

            <!-- 其他语言翻译 -->
            ${this.transLangOptions.length > 0 ? html`
              <div class="border-t border-[#e5e5ea] pt-4">
                <label class="block text-sm font-medium text-[#1c2024] mb-1.5">产品翻译</label>
                <p class="text-xs text-[#8b8d98] mb-3">默认语言为 English；只填写需要发布的语言，空白语言不会保存。</p>
                <div class="flex flex-wrap gap-2 mb-3">
                  ${this.transLangOptions.map(l => html`
                    <button type="button"
                      class="px-3 py-1 rounded text-sm border ${this.activeTransLang === l.code ? 'bg-[#0d74ce] text-white border-[#0d74ce]' : 'bg-white text-[#1c2024] border-[#d9d9e0]'}"
                      @click=${() => this.setActiveTransLang(l.code)}>
                      ${l.label} ${this.hasTranslation(l.code) ? html`<span aria-label="已填写">✓</span>` : html`<span class="opacity-50">·</span>`}
                    </button>
                  `)}
                </div>
                <div class="rounded border border-[#e5e5ea] bg-[#fafafa] px-3 py-2 text-xs text-[#60646c] mb-3">
                  当前编辑：${this.transLangOptions.find(l => l.code === this.activeTransLang)?.label || ''}
                  ${this.hasTranslation(this.activeTransLang) ? html`<span class="text-[#0d74ce] ml-1">已填写</span>` : html`<span class="text-[#b0b4ba] ml-1">未填写</span>`}
                </div>
                <div class="space-y-3">
                  <div>
                    <label class="block text-xs text-[#60646c] mb-1">产品名称（${this.transLangOptions.find(l => l.code === this.activeTransLang)?.label || ''}）</label>
                    <input type="text" .value=${this.activeTrans.name}
                      @input=${(e: Event) => this.updateTransField('name', (e.target as HTMLInputElement).value)}
                      class="w-full px-3 py-1.5 border border-[#d9d9e0] rounded text-sm outline-none focus:border-[#0d74ce]"
                      placeholder="输入该语言的产品名称" />
                  </div>
                  <div>
                    <label class="block text-xs text-[#60646c] mb-1">产品描述</label>
                    <crm-product-editor
                      .value=${this.activeTrans.description}
                      .minHeight=${180}
                      placeholder="输入该语言的产品描述..."
                      @input=${(e: Event) => this.updateTransField('description', (e as CustomEvent).detail.value as string)}></crm-product-editor>
                  </div>
                </div>
              </div>
            ` : ''}

            <!-- 图片 -->
            <div>
              <label class="block text-sm font-medium text-[#1c2024] mb-1.5">
                产品图片
                <span class="text-[#b0b4ba] font-normal ml-1">(${activeImageCount}/${MAX_IMAGES})</span>
              </label>
              <div class="flex flex-wrap gap-3 mb-3">
                ${this.panelImages.filter(i => !this.removedImageIds.includes(i.id)).map((img, idx) => html`
                  <div class="relative" draggable="true"
                    @dragstart=${() => this.handleImageDragStart(idx)}
                    @dragover=${(e: DragEvent) => e.preventDefault()}
                    @drop=${() => this.handleImageDrop(idx)}>
                    <img src="${API_BASE_URL}/products/images/${img.filename}"
                      class="image-thumb ${img.is_primary ? 'primary' : ''}" />
                    <button @click=${() => this.markRemovedImage(img.id)}
                      class="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#eb8e90] text-white rounded-full text-xs flex items-center justify-center hover:opacity-80">&times;</button>
                    ${img.is_primary ? html`<div class="text-[10px] text-[#0d74ce] text-center mt-0.5">主图</div>` : ''}
                  </div>
                `)}
                ${this.pendingImageFiles.map((pf, idx) => html`
                  <div class="relative">
                    <img src=${pf.previewUrl} class="image-thumb" />
                    <button @click=${() => this.removePendingImage(idx)}
                      class="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#eb8e90] text-white rounded-full text-xs flex items-center justify-center">&times;</button>
                  </div>
                `)}
                ${activeImageCount < MAX_IMAGES ? html`
                  <label class="image-thumb bg-[#f0f0f3] flex flex-col items-center justify-center cursor-pointer text-[#b0b4ba] hover:bg-[#e0e1e6] transition-colors">
                    <span class="text-xl">+</span>
                    <span class="text-[10px]">上传</span>
                    <input type="file" accept="image/*" multiple hidden @change=${this.handleImageSelect} />
                  </label>
                ` : ''}
              </div>
              <div class="text-[10px] text-[#b0b4ba]">拖拽可排序，蓝色边框为主图</div>
            </div>

            <!-- 文档 -->
            <div>
              <label class="block text-sm font-medium text-[#1c2024] mb-1.5">文档附件</label>
              <div class="space-y-1.5 mb-3">
                ${this.panelDocs.filter(d => !this.removedDocIds.includes(d.id)).map(doc => html`
                  <div class="flex items-center justify-between px-3 py-2 bg-[#f0f0f3] rounded">
                    <span class="text-sm text-[#1c2024] truncate flex-1">${doc.filename}</span>
                    <span class="text-xs text-[#b0b4ba] mr-3">${(doc.file_size / 1024).toFixed(1)}KB</span>
                    <button @click=${() => this.markRemovedDoc(doc.id)}
                      class="text-[#eb8e90] text-xs hover:underline flex-shrink-0">删除</button>
                  </div>
                `)}
                ${this.pendingDocFiles.map((f, idx) => html`
                  <div class="flex items-center justify-between px-3 py-2 bg-[#f0f0f3] rounded">
                    <span class="text-sm text-[#0d74ce] truncate flex-1">${f.name}</span>
                    <span class="text-xs text-[#0d74ce] mr-3">待上传</span>
                    <button @click=${() => this.removePendingDoc(idx)}
                      class="text-[#eb8e90] text-xs hover:underline flex-shrink-0">删除</button>
                  </div>
                `)}
                ${this.panelDocs.length + this.pendingDocFiles.length - this.removedDocIds.length === 0 ? html`
                  <div class="text-sm text-[#b0b4ba] py-3 text-center">暂无文档</div>
                ` : ''}
              </div>
              <label class="inline-flex items-center gap-1.5 px-4 py-1.5 border border-[#d9d9e0] rounded text-sm text-[#60646c] cursor-pointer hover:border-[#b0b4ba]">
                <span>+ 上传文档</span>
                <input type="file" hidden @change=${this.handleDocSelect} />
              </label>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="flex items-center justify-between pt-5 mt-2 border-t border-[#e0e1e6] flex-shrink-0">
            <div>
              ${this.editingId ? html`
                <button @click=${() => { this.closePanel(); this.deleteProduct(this.editingId); }}
                  class="px-4 py-1.5 text-[#eb8e90] text-sm rounded border border-[#eb8e90] hover:bg-[#fef2f2] transition-colors">删除产品</button>
              ` : ''}
            </div>
            <div class="flex gap-2">
              <button @click=${this.closePanel}
                class="px-5 py-1.5 text-[#60646c] text-sm rounded border border-[#d9d9e0] hover:bg-[#f0f0f3]">取消</button>
              <button @click=${this.saveProduct}
                class="px-5 py-1.5 bg-[#000] text-white text-sm rounded font-medium hover:opacity-90">保存</button>
            </div>
          </div>
        </div>
      </crm-side-panel>
    `;
  }
}
